import _createForOfIteratorHelper from "@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import { Editor, Path } from 'slate';
import isUrl from 'is-url';
import ListUtils from '../editor-utils/block-element-utils/list-utils';
import CodeUtils from '../editor-utils/block-element-utils/code-utils';
import { isRangeCollapsed } from '../editor-utils/range-utils';
import TableUtils from '../editor-utils/block-element-utils/table-utils';
import getEventTransfer from '../custom/get-event-transfer';
import BlockquoteUtils from '../editor-utils/block-element-utils/blockquote-utils';
import FormulaUtils from '../editor-utils/block-element-utils/formula-utils';
import { htmlDeserializer } from '../../utils/deserialize-html';
import generateLinkElement from '../element-model/link';
import generateImageElement from '../element-model/image';
import { SfEditor } from '../custom/custom';
import EditorContext from '../editor-context';
var withBlock = function withBlock(editor) {
  var exec = editor.exec;
  var listUtils = new ListUtils(editor);
  var codeUtils = new CodeUtils(editor);
  var tableUtils = new TableUtils(editor);
  var blockquoteUtils = new BlockquoteUtils(editor);
  var formulaUtils = new FormulaUtils(editor);
  editor.exec = function (command) {
    var data = command.data;
    var selection = editor.selection;
    switch (command.type) {
      case 'set_header':
        Editor.setNodes(editor, {
          type: command.headerType
        });
        break;
      case 'insert_data':
        var _getEventTransfer = getEventTransfer(data),
          fragment = _getEventTransfer.fragment,
          type = _getEventTransfer.type,
          html = _getEventTransfer.html,
          text = _getEventTransfer.text;
        if (type === 'file') {
          exec(command);
          return;
        }

        // insert a link if text content is a url
        if (isUrl(text) && !codeUtils.isInCodeBlock()) {
          if (text.endsWith('png') || text.endsWith('PNG') || text.endsWith('jpg') || text.endsWith('JPG') || text.endsWith('gif') || text.endsWith('GIF')) {
            var img = generateImageElement({
              data: {
                src: text
              }
            });
            Editor.insertNodes(editor, img);
            return;
          }
          var _EditorContext$getEdi = EditorContext.getEditor(),
            editorApi = _EditorContext$getEdi.editorApi;
          if (editorApi.isInternalFileLink(text)) {
            var index = text.lastIndexOf('/');
            if (index > 0) {
              var name = text.substring(index + 1);
              var fileName = decodeURIComponent(name);
              SfEditor.insertNodes(editor, [generateLinkElement({
                data: {
                  href: text
                },
                children: [{
                  text: fileName
                }]
              }), {
                text: ' '
              }]);
              return;
            }
          }
          if (editorApi.isInternalDirLink(text)) {
            var _index = text.lastIndexOf('/');
            if (_index > 0) {
              var name = text.substring(_index + 1);
              var dirName = decodeURIComponent(name);
              SfEditor.insertNodes(editor, [generateLinkElement({
                data: {
                  href: text
                },
                children: [{
                  text: dirName
                }]
              }), {
                text: ' '
              }]);
              return;
            }
          }
          SfEditor.insertNodes(editor, [generateLinkElement({
            data: {
              href: text
            },
            children: [{
              text: text
            }]
          }), {
            text: ' '
          }]);
          return;
        }
        try {
          if (tableUtils.isInTable()) {
            tableUtils.pasteContentInTable(command);
            return;
          }
          if (listUtils.isInlist()) {
            listUtils.pasteContentInList(command);
            return;
          }
          if (codeUtils.isInCodeBlock()) {
            codeUtils.pasteContentInCodeBlock(command);
            return;
          }
          if (blockquoteUtils.isInBlockquote()) {
            blockquoteUtils.pasteContentInBlockquote(command);
            return;
          }
        } catch (error) {
          Editor.insertText(editor, text);
        }
        if (type === 'fragment') {
          var _Editor$match = Editor.match(editor, editor.selection, 'block'),
            _Editor$match2 = _slicedToArray(_Editor$match, 2),
            currentBlock = _Editor$match2[0],
            path = _Editor$match2[1];
          var firstFragmentNode = fragment[0];
          // if first fragment node is paragraph insert the content of it into previous sibling
          // and insert other node
          if (firstFragmentNode.type === 'paragraph') {
            Editor.insertFragment(editor, fragment);
            return;
          }

          /** 
           * if the first fragment element is a list, get normalized list items with function getNormalizedListItems and insert
           */
          if (firstFragmentNode.type.includes('_list')) {
            var listItems = listUtils.getNormalizedListItems(firstFragmentNode);
            var newFragment = fragment.slice(1);
            Editor.insertNodes(editor, [{
              type: firstFragmentNode.type,
              children: listItems
            }].concat(_toConsumableArray(newFragment)));
            if (SfEditor.isEmptyParagraph(currentBlock)) {
              Editor.delete(editor, {
                at: path
              });
            }
            return;
          }

          // if current node is an empty paragraph, insert the fragment and
          // delete the empty paragraph
          if (SfEditor.isEmptyParagraph(currentBlock)) {
            Editor.insertNodes(editor, fragment);
            Editor.delete(editor, {
              at: path
            });
            return;
          }
          // insert the fragment below the current selection node
          Editor.insertNodes(editor, fragment);
          return;
        }
        if (type === 'html') {
          try {
            var _fragment = htmlDeserializer(html);
            Editor.insertFragment(editor, _fragment);
          } catch (error) {
            var newText = text.replace(/\n|\r\n/g, ' ');
            Editor.insertText(editor, newText);
          }
          return;
        }

        // remove text line break and insert text
        if (type === 'text') {
          var textArray = text.split(/\r\n|\n/g);
          textArray.forEach(function (text, index) {
            if (index === 0) {
              Editor.insertText(editor, text);
            } else {
              Editor.insertNodes(editor, {
                type: 'paragraph',
                children: [{
                  text: text
                }]
              });
            }
          });
          return;
        }
        exec(command);
        break;
      case 'set_blockquote':
        if (!selection) {
          return;
        }
        blockquoteUtils.wrapBlockquote();
        break;
      case 'unwrap_blockquote':
        blockquoteUtils.unwrapBlockquote();
        break;
      case 'set_ordered_list':
        if (!selection) {
          return;
        }
        listUtils.wrapList('ordered_list');
        break;
      case 'set_unordered_list':
        if (!selection) {
          return;
        }
        listUtils.wrapList('unordered_list');
        break;
      case 'unwrap_unordered_list':
      case 'unwrap_ordered_list':
        listUtils.unwrapList();
        break;
      case 'wrap_check_list_item':
        var listItem = listUtils.getCurrentListItem();
        if (listItem.length === 0) {
          // not in list, add list
          listUtils.wrapList('unordered_list');
          Editor.setNodes(editor, {
            data: {
              checked: false
            }
          }, {
            match: {
              type: 'list_item'
            }
          });
        } else {
          var _listItems = Editor.nodes(editor, {
            at: editor.selection,
            match: {
              type: 'list_item'
            }
          });
          var nearestItem;
          var _iterator = _createForOfIteratorHelper(_listItems),
            _step;
          try {
            for (_iterator.s(); !(_step = _iterator.n()).done;) {
              var _listItem = _step.value;
              nearestItem = _listItem;
            }
          } catch (err) {
            _iterator.e(err);
          } finally {
            _iterator.f();
          }
          Editor.setNodes(editor, {
            data: {
              checked: false
            }
          }, {
            at: nearestItem[1]
          });
        }
        break;
      case 'unwrap_check_list_item':
        var item = listUtils.getCurrentListItem();
        Editor.setNodes(editor, {
          data: {}
        }, {
          at: item[0][1]
        });
        break;
      case 'modify_sublist_type':
        var target_list_type = command.target_list_type;
        var items = listUtils.getCurrentListItem();
        var firstListItem = items.at(0);
        var currentListPath = Path.parent(firstListItem[1]);
        var parentPath = Path.parent(currentListPath);
        Editor.withoutNormalizing(editor, function () {
          for (var i = items.length - 1; i >= 0; i--) {
            var _item = items[i];
            var _path = _item[1];
            Editor.liftNodes(editor, {
              at: _path,
              split: true
            });
            var currentListIndex = currentListPath.at(-1);
            var currentListItemIndex = _path.at(-1);
            Editor.wrapNodes(editor, {
              type: target_list_type,
              children: []
            }, {
              at: [].concat(_toConsumableArray(parentPath), [currentListItemIndex === 0 ? currentListIndex : currentListIndex + 1]),
              split: true
            });
          }
        });
        break;
      case 'set_code_block':
        codeUtils.wrapCodeBlock();
        break;
      case 'unwrap_code_block':
        codeUtils.unwrapCodeBlock();
        break;
      case 'exit_code_block':
        Editor.splitNodes(editor, {
          match: 'block',
          always: true
        });
        codeUtils.unwrapCodeBlock(true);
        break;
      case 'increase_code_block_indent':
        if (isRangeCollapsed(editor.selection)) {
          var block = Editor.match(editor, editor.selection, 'block');
          Editor.insertText(editor, '\t', {
            at: {
              anchor: {
                path: [].concat(_toConsumableArray(block[1]), [0]),
                offset: 0
              },
              focus: {
                path: [].concat(_toConsumableArray(block[1]), [0]),
                offset: 0
              }
            }
          });
        }
        break;
      case 'insert_table':
        tableUtils.insertTable(data);
        break;
      case 'remove_table':
        tableUtils.removeTable();
        break;
      case 'insert_row':
        tableUtils.insertRow();
        break;
      case 'insert_column':
        tableUtils.insertColumn();
        break;
      case 'remove_row':
        tableUtils.removeRow();
        break;
      case 'remove_column':
        tableUtils.removeColumn();
        break;
      case 'set_table_cell_align':
        tableUtils.setAlign(command.align);
        break;
      case 'exit_table':
        tableUtils.exitTable();
        break;
      case 'focus_next_table_cell':
        tableUtils.focusNextTableCell();
        break;
      case 'focus_previous_table_cell':
        tableUtils.focusPreviousTableCell();
        break;
      case 'insert_formula':
        formulaUtils.insertFormula(command.data);
        break;
      case 'set_formula':
        formulaUtils.setFormula(command.data);
        break;
      default:
        exec(command);
        break;
    }
  };
  return editor;
};
export default withBlock;